/**
 * syncCitationOrder.js
 *
 * Traverses the ProseMirror document tree, finds every citation mark in
 * document order, assigns sequential numbers, and:
 *  1. Patches any mark whose citationText is out-of-date via a ProseMirror tr
 *  2. Re-saves the reference list to localStorage in the new order
 *  3. Fires the 'referencesUpdated' custom event so Bibliography re-renders
 *
 * This is called from the appendTransaction ProseMirror plugin so it runs
 * automatically after each edit.  It is debounced externally.
 */

import { getActiveStyle, fetchStyleXML, generateInTextCitation, generateGroupedInTextCitations, generateBibliography, getCachedStyleXML } from './CitationStyleService';

/**
 * Core sync function.
 *
 * @param {EditorView} view  – ProseMirror EditorView
 */
export async function syncCitationOrder(view) {
  const activeStyle = getActiveStyle();
  // Fetch style XML (async) – handles caching in-memory
  let styleXML = null;
  try {
    styleXML = await fetchStyleXML(activeStyle.id);
  } catch (err) {
    console.warn(`Failed to fetch CSL XML for style ${activeStyle.id}:`, err);
  }

  const { state } = view;
  const { doc, schema } = state;

  if (!schema.marks.citation) return;

  const docId = localStorage.getItem('documentSelected');
  if (!docId) return;

  const storedRefs = JSON.parse(localStorage.getItem(`${docId}_references`) || '[]');
  if (storedRefs.length === 0) return;

  // ── Step 1: collect citation marks in document order ──────────────────────
  // Map: referenceId → first-seen sequence number
  const seenIds = new Map();   // referenceId → sequenceNumber (1-based)
  let counter = 0;

  // Also record positions of nodes that carry citation marks so we can patch them
  const markedPositions = []; // { from, to, citationId, referenceIds, currentText }

  doc.descendants((node, pos) => {
    if (!node.isText) return;
    node.marks.forEach((mark) => {
      if (mark.type !== schema.marks.citation) return;

      const { citationId, citationText } = mark.attrs;

      // The CitationTool stores one citationId per mark, but citationId maps to
      // one or more referenceIds (a citation group).
      const storedCitations = JSON.parse(
        localStorage.getItem(`${docId}_citations`) || '[]'
      );
      const citGroup = storedCitations.find((c) => c.id === citationId);
      const refIds = citGroup ? citGroup.referenceIds : [];

      // Assign sequential numbers to each referenced referenceId
      refIds.forEach((rid) => {
        if (!seenIds.has(rid)) {
          seenIds.set(rid, ++counter);
        }
      });

      markedPositions.push({
        from: pos,
        to: pos + node.nodeSize,
        citationId,
        citationText,
        refIds,
      });
    });
  });

  if (seenIds.size === 0) {
    const orderedRefs = [];
    storedRefs.forEach((ref) => {
      orderedRefs.push({ ...ref, number: orderedRefs.length + 1, isCited: false });
    });
    localStorage.setItem(`${docId}_references`, JSON.stringify(orderedRefs));
    window.dispatchEvent(new Event('referencesUpdated'));
    return;
  }

  // ── Step 2: build the new formatted citation texts ────────────────────────
  // Use a single engine to generate continuous formatted text for all citation marks
  const clusters = markedPositions.map(pos => ({ citationId: pos.citationId, refIds: pos.refIds }));
  const generatedTexts = generateGroupedInTextCitations(clusters, styleXML, storedRefs);

  const buildCitationText = (pos, index) => {
    if (generatedTexts && generatedTexts[index]) {
      return generatedTexts[index];
    }
    
    // Fallback if CSL fails
    const refs = pos.refIds
      .map((rid) => {
        const ref = storedRefs.find((r) => r.id === rid);
        if (!ref) return null;
        return { ...ref, number: seenIds.get(rid) };
      })
      .filter(Boolean);

    if (refs.length === 0) return '[?]';
    const numbers = refs.map((r) => r.number || '?');
    return `[${numbers.join(', ')}]`;
  };

  // ── Step 3: dispatch a single transaction patching stale citation nodes ───
  let tr = state.tr;
  let offset = 0; // running offset as we replace text (which may change sizes)

  for (let i = 0; i < markedPositions.length; i++) {
    const pos = markedPositions[i];
    const newText = buildCitationText(pos, i);
    if (newText === pos.citationText) continue; // already correct

    const newMark = schema.marks.citation.create({
      citationId: pos.citationId,
      citationText: newText,
    });

    const adjFrom = pos.from + offset;
    const adjTo   = pos.to   + offset;

    const newNode = schema.text(newText, [newMark]);
    tr = tr.replaceWith(adjFrom, adjTo, newNode);

    // Update offset: difference between new text length and old text length
    offset += newText.length - (pos.to - pos.from);
  }

  // ── Step 4: reorder reference list in localStorage ────────────────────────
  // Build a new list in citation-appearance order, then append any unused refs
  const orderedRefs = [];
  seenIds.forEach((seqNum, rid) => {
    const ref = storedRefs.find((r) => r.id === rid);
    if (ref) orderedRefs.push({ ...ref, number: seqNum, isCited: true });
  });

  // Refs that are in the library but not cited yet keep their position at the end
  const unusedRefs = [];
  storedRefs.forEach((ref) => {
    if (!seenIds.has(ref.id)) {
      unusedRefs.push({ ...ref, number: orderedRefs.length + unusedRefs.length + 1, isCited: false });
    }
  });
  
  const finalRefs = [...orderedRefs, ...unusedRefs];
  localStorage.setItem(`${docId}_references`, JSON.stringify(finalRefs));

  // ── Step 5: Update references section in document ───────────────────────
  const bibResult = generateBibliography(orderedRefs, styleXML);
  const entries = bibResult ? bibResult.entries : [];
    
  // Find "References" heading OR custom bibliography node
  // We scan for the FIRST occurrence of a heading with text "References"
  let firstRefPos = -1;
  tr.doc.descendants((node, pos) => {
    if (firstRefPos !== -1) return false; // stop once found
    
    const nodeType = node.type.name;
    const text = node.textContent.trim();
    
    // Catch-all for various heading/title node names and "References" variants
    const isHeading = nodeType.includes('heading') || nodeType === 'title';
    const isReferencesText = text === 'References' || text === 'Bibliography';
    
    if (nodeType === 'bibliography' || (isHeading && isReferencesText)) {
      firstRefPos = pos;
      return false;
    }
  });

  // Always clear any existing sections if found
  if (firstRefPos !== -1) {
    // Delete from the start of the references section to the absolute end of the document
    tr = tr.delete(firstRefPos, tr.doc.content.size);
  }

  // Only insert if there are actually cited references
  if (orderedRefs.length > 0) {
    const nodesToInsert = [];
    const headingType = schema.nodes.heading || schema.nodes.title || schema.nodes.heading1;
    if (headingType) {
      nodesToInsert.push(headingType.create({ level: 2 }, [schema.text('References')]));
    }

    if (entries.length > 0) {
      entries.forEach(html => {
        const cleanText = html.replace(/<\/?[^>]+(>|$)/g, ""); 
        const p = schema.nodes.paragraph.create({}, [schema.text(cleanText)]);
        nodesToInsert.push(p);
      });
    } else {
      // Fallback hand-coded formatter if CSL failed
      orderedRefs.forEach(ref => {
        let text = `[${ref.number}] ${ref.authors} (${ref.year}). ${ref.title}.`;
        if (ref.journal) text += ` ${ref.journal}.`;
        const p = schema.nodes.paragraph.create({}, [schema.text(text)]);
        nodesToInsert.push(p);
      });
    }

    // Append to the clean end of the document
    tr = tr.insert(tr.doc.content.size, nodesToInsert);
  }

  if (tr.docChanged) {
    tr.setMeta('addToHistory', false);
    view.dispatch(tr);
  }


  window.dispatchEvent(new Event('referencesUpdated'));
}

/**
 * Simple debounce helper (returns a debounced version of fn).
 */
export function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}
